import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Address from "_shared/addresses/address";
import AddressSchema from "_shared/addresses/address-schema";
import { useState } from "react";
import { ZodError } from "zod";

export type ManualAddressDrawerProps = {
  open: boolean;
  value: Address | null;
  onClose: () => void;
  onAccept: (address: Address) => void;
};

type ManualAddressDraft = {
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

type ManualAddressField = keyof ManualAddressDraft;
type ManualAddressErrors = Partial<Record<ManualAddressField, string>>;

const EMPTY_MANUAL_ADDRESS: ManualAddressDraft = {
  address1: "",
  address2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
};

const StyledManualAddressDrawer = styled(Drawer)(({ theme }) => ({
  zIndex: theme.zIndex.modal + 1,
  "& .MuiDrawer-paper": {
    boxSizing: "border-box",
    maxWidth: "100vw",
    padding: theme.spacing(4),
    width: theme.spacing(52),
  },
}));

const ManualAddressPanel = styled("section")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(0.25),
  height: "60%",
  overflowY: "auto",
}));

const ManualAddressActions = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

const UnsavedChangesPrompt = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.25),
}));

const UnsavedChangesActions = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const AddressTextField = styled(TextField)(() => ({
  width: "100%",
}));

const BackButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const AcceptButton = styled(Button)(() => ({
  height: 40,
  minHeight: 40,
  paddingTop: 0,
  paddingBottom: 0,
}));

const AcceptButtonWrapper = styled("span")(() => ({
  display: "block",
}));

const DrawerTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
}));

function buildManualAddressDraft(address?: Address | null): ManualAddressDraft {
  if (!address) return { ...EMPTY_MANUAL_ADDRESS };

  return {
    address1: address.address1 ?? "",
    address2: address.address2 ?? "",
    city: address.city ?? "",
    state: address.state ?? "",
    postalCode: address.postalCode ?? "",
    country: address.country ?? "",
  };
}

function isDraftEqual(left: ManualAddressDraft, right: ManualAddressDraft): boolean {
  return (
    left.address1 === right.address1 &&
    left.address2 === right.address2 &&
    left.city === right.city &&
    left.state === right.state &&
    left.postalCode === right.postalCode &&
    left.country === right.country
  );
}

function parseSchemaErrors(error: unknown): ManualAddressErrors {
  if (!(error instanceof Error) || !(error.cause instanceof ZodError)) return {};

  const fieldErrors: ManualAddressErrors = {};
  for (const issue of error.cause.issues) {
    const field = issue.path[0];
    if (typeof field === "string" && field in EMPTY_MANUAL_ADDRESS && !fieldErrors[field as ManualAddressField]) {
      fieldErrors[field as ManualAddressField] = issue.message;
    }
  }
  return fieldErrors;
}

function buildManualAddressParams(draft: ManualAddressDraft) {
  return {
    address1: draft.address1.trim(),
    address2: draft.address2.trim() || undefined,
    city: draft.city.trim() || undefined,
    state: draft.state.trim() || undefined,
    postalCode: draft.postalCode.trim() || undefined,
    country: draft.country.trim() || undefined,
    coordinates: {
      latitude: "0",
      longitude: "0",
    },
  };
}

function validateManualAddress(draft: ManualAddressDraft): ManualAddressErrors {
  const params = buildManualAddressParams(draft);
  const errors: ManualAddressErrors = {};

  if (!draft.address1.trim()) errors.address1 = "Address line 1 is required";
  if (!draft.city.trim()) errors.city = "City is required";
  if (!draft.state.trim()) errors.state = "State is required";
  if (!draft.postalCode.trim()) errors.postalCode = "Postal code is required";
  if (!draft.country.trim()) errors.country = "Country is required";

  try {
    AddressSchema.validate(params);
  } catch (error) {
    const parsedErrors = parseSchemaErrors(error);
    for (const [field, message] of Object.entries(parsedErrors)) {
      const manualField = field as ManualAddressField;
      if (!errors[manualField] && message) {
        errors[manualField] = message;
      }
    }
  }

  return errors;
}

export default function ManualAddressDrawer(props: Readonly<ManualAddressDrawerProps>) {
  const [manualAddressDraft, setManualAddressDraft] = useState<ManualAddressDraft>(buildManualAddressDraft(props.value));
  const [manualAddressErrors, setManualAddressErrors] = useState<ManualAddressErrors>({});
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);

  function closeWithoutSaving() {
    setShowUnsavedPrompt(false);
    setManualAddressDraft(buildManualAddressDraft(props.value));
    setManualAddressErrors({});
    props.onClose();
  }

  function handleClose() {
    const originalDraft = buildManualAddressDraft(props.value);
    if (isDraftEqual(manualAddressDraft, originalDraft)) {
      closeWithoutSaving();
      return;
    }

    setShowUnsavedPrompt(true);
  }

  function handleManualAddressFieldChange(field: ManualAddressField, value: string) {
    setManualAddressDraft((previous) => {
      const next = {
        ...previous,
        [field]: value,
      };

      if (manualAddressErrors[field]) {
        setManualAddressErrors(validateManualAddress(next));
      }

      return next;
    });
  }

  function handleAccept() {
    setShowUnsavedPrompt(false);
    const nextErrors = validateManualAddress(manualAddressDraft);
    setManualAddressErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const manualAddress = Address.create(buildManualAddressParams(manualAddressDraft));
    props.onAccept(manualAddress);
  }

  return (
    <StyledManualAddressDrawer open={props.open} anchor="right" onClose={handleClose}>
      <ManualAddressPanel>
        <DrawerTitle variant="h6">Enter Address</DrawerTitle>
        <AddressTextField
          label="Address Line 1"
          value={manualAddressDraft.address1}
          error={Boolean(manualAddressErrors.address1)}
          helperText={manualAddressErrors.address1}
          onChange={(event) => handleManualAddressFieldChange("address1", event.target.value)}
          onBlur={() => setManualAddressErrors(validateManualAddress(manualAddressDraft))}
          required
        />
        <AddressTextField
          label="Address Line 2"
          value={manualAddressDraft.address2}
          error={Boolean(manualAddressErrors.address2)}
          helperText={manualAddressErrors.address2}
          onChange={(event) => handleManualAddressFieldChange("address2", event.target.value)}
        />
        <AddressTextField
          label="City"
          value={manualAddressDraft.city}
          error={Boolean(manualAddressErrors.city)}
          helperText={manualAddressErrors.city}
          onChange={(event) => handleManualAddressFieldChange("city", event.target.value)}
          onBlur={() => setManualAddressErrors(validateManualAddress(manualAddressDraft))}
          required
        />
        <AddressTextField
          label="State"
          value={manualAddressDraft.state}
          error={Boolean(manualAddressErrors.state)}
          helperText={manualAddressErrors.state}
          onChange={(event) => handleManualAddressFieldChange("state", event.target.value)}
          onBlur={() => setManualAddressErrors(validateManualAddress(manualAddressDraft))}
          required
        />
        <AddressTextField
          label="Postal Code"
          value={manualAddressDraft.postalCode}
          error={Boolean(manualAddressErrors.postalCode)}
          helperText={manualAddressErrors.postalCode}
          onChange={(event) => handleManualAddressFieldChange("postalCode", event.target.value)}
          onBlur={() => setManualAddressErrors(validateManualAddress(manualAddressDraft))}
          required
        />
        <AddressTextField
          label="Country"
          value={manualAddressDraft.country}
          error={Boolean(manualAddressErrors.country)}
          helperText={manualAddressErrors.country}
          onChange={(event) => handleManualAddressFieldChange("country", event.target.value)}
          onBlur={() => setManualAddressErrors(validateManualAddress(manualAddressDraft))}
          required
        />
        {!showUnsavedPrompt && (
          <ManualAddressActions>
            <BackButton
              size="small"
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={handleClose}
            >
              Back
            </BackButton>
            <Tooltip
              title={
                Object.keys(manualAddressErrors).length > 0
                  ? Object.values(manualAddressErrors).map((error, index) => (
                      <div key={index}>{error}</div>
                    ))
                  : ""
              }
              arrow
            >
              <AcceptButtonWrapper>
                <AcceptButton
                  size="medium"
                  variant="contained"
                  startIcon={<CheckIcon />}
                  onClick={handleAccept}
                  disabled={Object.keys(manualAddressErrors).length > 0}
                >
                  Accept
                </AcceptButton>
              </AcceptButtonWrapper>
            </Tooltip>
          </ManualAddressActions>
        )}
        {showUnsavedPrompt && (
          <UnsavedChangesPrompt variant="outlined">
            <Typography variant="body2">Save your address changes before closing?</Typography>
            <UnsavedChangesActions>
              <Button size="small" onClick={() => setShowUnsavedPrompt(false)}>
                Keep Editing
              </Button>
              <Button size="small" onClick={closeWithoutSaving}>
                Discard
              </Button>
              <Button size="small" variant="contained" onClick={handleAccept}>
                Save
              </Button>
            </UnsavedChangesActions>
          </UnsavedChangesPrompt>
        )}
      </ManualAddressPanel>
    </StyledManualAddressDrawer>
  );
}
